import Marionette from "../../shared/marionette-mustache";
import {autoinject} from "aurelia-dependency-injection";
import {backboneViewProperties} from "../../decorators/backbone-properties";
import {AppEvents} from "../../app-events";
import {BrowseModel} from "../models/browse";
import {BrowsePanelModel} from "../models/browse-panel";
import {BrowsePanelsCollection} from "../collections/browse-panels";
import {IRequestOptions} from "../../search/interfaces/request-options";

const template = require<string>("../../browse/views/browse.html");
const dom = {
   BROWSE_LINKS : ".browse-link",
   BROWSE_TABS : ".browse-tab",
   BROWSE_SEARCH: "#browse-search",
   BROWSE_CLEAR: "#browse-clear",
   BROWSE_SCROLL_LEFT: "#browse-scroller-left-icon",
   BROWSE_SCROLL_RIGHT: "#browse-scroller-right-icon",
   BROWSE_FULL_TEXT_ONLY: "#browse-full-text-only",
   BROWSE_SUBSCRIPTION_ONLY: "#browse-subscription-only",
   BROWSE_PANELS: ".browse-panels",
   BROWSE_PANEL_FIRST: ".browse-panel:first",
   BROWSE_BREADCRUMB: "#browse-breadcrumb",
   BROWSE_BREADCRUMB_CONTAINER: ".browse-breadcrumb-container",
   BROWSE_BREADCRUMB_OUTER_CONTAINER: ".browse-breadcrumb-outer-container"
};

@autoinject
@backboneViewProperties({
    template: template
})
export class BrowseView extends Marionette.LayoutView<BrowseModel> {

    private panelsView: BrowsePanelsCollection;
    private panelsRegion: Marionette.Region;

    constructor(
        private appEvents: Backbone.Events,
        public model: BrowseModel) {
        super();
        this.$el = $(this.el);
        this.setupModelListeners();
    }

    public onShow() {
        this.setupPanels();
        this.setMaxPanels();
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click " + dom.BROWSE_TABS] = (e) => this.browseTabClick(e);
        events["click " + dom.BROWSE_SEARCH] = (e) => this.browseSearchClick();
        events["click " + dom.BROWSE_FULL_TEXT_ONLY] = (e) => this.browseFullTextOnlyClick(e);
        events["click " + dom.BROWSE_SUBSCRIPTION_ONLY] = (e) => this.browseSubscriptionOnlyClick(e);
        events["click " + dom.BROWSE_CLEAR] = (e) => this.browseClear(e);
        events["click " + dom.BROWSE_SCROLL_LEFT] = () => this.browseScrollLeft();
        events["click " + dom.BROWSE_SCROLL_RIGHT] = () => this.browseScrollRight();
        return events;
    }

    public regions(): any {
        const regions: any = {
            panelsRegion: ".browse-panels",
        };
        return regions;
    }

    public panelsChanged() {
        const $breadcrumb = this.$(dom.BROWSE_BREADCRUMB);
        $breadcrumb.empty();
        let lastValidPanel;
        if (this.$(dom.BROWSE_LINKS).hasClass("active")) {
            this.model.Panels.each((panel) => {
                if (panel.Value) {
                    const item = panel.ListItems.where({Id: panel.Value})[0]; //
                    if (item) {
                        if (item.get("Enabled")) {
                            const text = item.get("Title");
                            const $element = $(`<li><a>${text} <b class="caret" aria-hidden="true"></b></a></li>`);
                            $breadcrumb.append($element);
                            this.appEvents.trigger(AppEvents.AppInsightsBrowsePanelLinkClick, {PanelName: text, PanelType: panel.SelectedType});
                        } else {
                            lastValidPanel = panel;
                            return false;
                        }
                    }
                }
            });
            if (lastValidPanel) {
                const index = this.model.Panels.indexOf(lastValidPanel);
                this.model.Panels.reset(this.model.Panels.first(index));
            }
        }
        this.showOrHideButtons();
        this.updateDocumentCount();
    }

    private updateDocumentCount() {
        const last = _.last(this.model.Panels.filter(((p) => typeof(p.Value) !== "undefined")));
        if (last) {
            const selected = last.ListItems.findWhere({Id : last.Value});
            if (selected) {
                const count = selected.get("NumDocuments");
                const countString = selected.get("NumDocumentsFormatted");
                this.setDocumentCount(count, countString);
            }
        }
    }

    private setupPanels() {
        if (!this.panelsView) {
            this.panelsView = new BrowsePanelsCollection({ collection: this.model.Panels});
            this.panelsRegion.show(this.panelsView);
        }
    }

    private setupModelListeners() {
        this.listenTo(this.model.Panels, "change", this.panelsChanged);
        this.listenTo(this.model, "change:DocumentCount", this.documentCountChanged);
        this.listenTo(this.model, "change:PanelsShiftValue", this.panelsShiftValueChanged);
        this.appEvents.on(AppEvents.WindowResized, () => { this.setMaxPanels(); this.panelsChanged(); });
    }

    private setDocumentCount(count: number, countString: string) {
        this.model.DocumentCount = {count, countString};
    }

    private documentCountChanged(model, prop: {count, countString}) {
        this.$(dom.BROWSE_SEARCH).text(`Show document${prop.count > 1 ? "s" : ""}`);
    }

    private getPanelValue(panel: BrowsePanelModel) {
        switch (panel.SelectedType) {
            case "material":
                const materialTypes = [];
                switch (panel.Value) {
                    case "statute":
                        materialTypes.push("Acts", "Legislation");
                        break;
                    case "si":
                        materialTypes.push("SI");
                        break;
                    case "eu":
                        materialTypes.push("EU");
                        break;
                    case "case":
                        materialTypes.push("Case");
                        break;
                }
                return materialTypes;
            case "reports":
                return panel.ListItems.where({Id: panel.Value})[0].get("Code");
            case "category":
                return panel.ListItems.where({Id: panel.Value})[0].get("Id");
            case "descriptor":
                return panel.Value.substring(1);
            case "court":
                return panel.ListItems.where({Id: panel.Value})[0].get("Id");
            case "jurisdiction":
                const value = panel.ListItems.where({Id: panel.Value})[0].get("Id");
                return value.endsWith("A") ? value.slice(0, -1) : value;
            case "eurovoc":
                if (panel.Value.startsWith("domain")) {
                    return panel.Value.substring("domain".length);
                } else if (panel.Value.startsWith("thesaurus")) {
                    return panel.Value.substring("thesaurus".length);
                } else {
                    return panel.Value.substring("descriptor".length);
                }
            default:
                return panel.Value;
        }
    }

    private getSearchFieldName(panel: BrowsePanelModel) {
        switch (panel.SelectedType) {
            case "material":
                return "MaterialType";
            case "year":
                return "Year";
            case "reportyear":
                return "ReportYear";
            case "jurisdiction":
                return "JurisdictionId";
            case "court":
                return "CourtId";
            case "category":
                return "CategoryId";
            case "reports":
                return "ReportSeries";
            case "sector":
                return "Sector";
            case "descriptor":
                return "Descriptor";
            case "eurovoc":
                return "EurovocId";
        }
    }

    private buildSearchFieldDictionary(panels: Backbone.Collection<BrowsePanelModel>) {
        const fieldDictionary = {};
        panels.each((panel) => {
            if (panel.Value) {
                fieldDictionary[this.getSearchFieldName(panel)] = this.getPanelValue(panel);
            }
        });
        return fieldDictionary;
    }

    private compileSearchQuery(fieldDictionary: any) {
        const searchParameters = [];
        Object.keys(fieldDictionary).forEach((key) => {
            let value = $.trim(fieldDictionary[key]);
            if (key === "MaterialType") {
                if (value === "Acts,Legislation") {
                    value = "Legislation";
                    searchParameters.push(`Indicators[WholeAct]`);
                } else if (value === "SI") {
                    value = "Legislation";
                    searchParameters.push(`Indicators[SI]`);
                } else if (value === "EU") {
                    searchParameters.push(`JurisdictionId[5]`);
                }
            }
            searchParameters.push(`${key}[\"${value}\"]`);
        });

        let query = searchParameters.join(" AND ");
        if (fieldDictionary.MaterialType[0] === "Case" && !fieldDictionary.CourtId) {
           query = `${query} AND NOT CourtId ["5"]`;
        }
        return query;
    }

    private browseSearchClick() {
        const fieldDictionary = this.buildSearchFieldDictionary(this.model.Panels);
        const searchQuery = this.compileSearchQuery(fieldDictionary);
        const materialTypeKey = "MaterialType";
        const options: IRequestOptions = {
            page: 1,
            query: searchQuery,
            order: "Relevance",
            materialTypes: fieldDictionary[materialTypeKey],
            includedCategoryFilters: [],
            showFullTextOnly: this.model.FullTextOnly
        };
        this.appEvents.trigger(AppEvents.AppInsightsBrowseFindCases, {query: options.query});
        this.appEvents.trigger(AppEvents.ClearFilters);
        this.appEvents.trigger(AppEvents.ClearSearchBox);
        this.appEvents.trigger(AppEvents.BrowseSearchRequest, options);
    }

    private browseFullTextOnlyClick(e: JQueryMouseEventObject) {
        this.model.FullTextOnly = $(e.currentTarget).prop("checked");
    }

    private browseSubscriptionOnlyClick(e: JQueryMouseEventObject) {
        this.model.SubscriptionOnly = $(e.currentTarget).prop("checked");
    }

    private browseClear(e: JQueryMouseEventObject) {
        const $breadcrumb = this.$(dom.BROWSE_BREADCRUMB);
        $breadcrumb.empty();

        this.$(dom.BROWSE_SEARCH).hide();

        this.$(dom.BROWSE_LINKS).removeClass("active");
        this.model.Panels.remove(this.model.Panels.rest(0));

        Backbone.history.navigate("browse", {trigger: true});
    }

    private browseTabClick(e: JQueryMouseEventObject) {
        e.preventDefault();
        const url = $(e.currentTarget).attr("href");
        Backbone.history.navigate(url, {trigger: true});
    }

    private showOrHideButtons() {
        if (this.$(dom.BROWSE_LINKS).hasClass("active")) {
            this.$(dom.BROWSE_CLEAR).removeAttr("disabled");
        }
        else {
            this.$(dom.BROWSE_CLEAR).attr("disabled", "true");
        }

        _.defer(() => {
            const numberOfActivePanels = this.model.Panels.length;
            if (numberOfActivePanels > 2) {
                this.$(dom.BROWSE_SEARCH).show();
            } else {
                this.$(dom.BROWSE_SEARCH).hide();
            }
        });
    }

    private browseScrollLeft() {
        this.model.PanelsShiftValue += this.model.PanelWidth;
    }

    private browseScrollRight() {
        this.model.PanelsShiftValue -= this.model.PanelWidth;
    }

    private panelsShiftValueChanged(model, property) {
        this.$(dom.BROWSE_PANELS).animate({left: property});
        this.$(dom.BROWSE_BREADCRUMB_CONTAINER).animate({left: property});

        this.setLeftPanelScrollerVisibility();
        this.setRightPanelScrollerVisibility();
    }

    private setLeftPanelScrollerVisibility() {
        if (this.model.Panels.size() > this.model.MaxPanels && this.model.PanelsShiftValue < 0) {
            this.$(dom.BROWSE_SCROLL_LEFT).show();
        } else {
            this.$(dom.BROWSE_SCROLL_LEFT).hide();
        }
    }

    private setRightPanelScrollerVisibility() {
        if (this.model.Panels.size() > this.model.MaxPanels && this.model.PanelsShiftValue > ((this.model.MaxPanels - this.model.Panels.size()) * this.model.PanelWidth)) {
            this.$(dom.BROWSE_SCROLL_RIGHT).show();
        } else {
            this.$(dom.BROWSE_SCROLL_RIGHT).hide();
        }
    }

    private setMaxPanels() {
        const viewPadding = 15;
        this.model.MaxPanels = Math.floor((this.$el.width() - viewPadding) / this.model.PanelWidth);
        this.model.setPanelShift();
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/browse/views/browse.ts